<script setup lang="ts">
  import {
    computed,
    onMounted,
    ref,
    watch,
    inject,
    defineAsyncComponent,
    type ComputedRef,
  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import { useCommonStore, useQuoteStore, useTravelersStore, useUserStore } from '@/shared/store'
  import {
    LOAD_EVENT_NAME,
    PROCEED_EVENT_NAME,
    PAYMENT_REDIRECT_HASH,
    PAYMENT_METHOD_VALUE,
    PAYMENT_VALUE_TO_TYPE,
  } from './FlowQuoteWidgetQC.constants'
  import {
    type GetTravelQuoteUaeResponse,
    type PaymentOption,
    type Country,
    CountryBoundType,
    PromoCodeTypes,
    WordingDocName,
    type GetTravelPricePayload,
  } from '@/shared/types/api.types'
  import AlertContainer from '@/shared/components/AlertContainer/AlertContainer.vue'
  import { useDebounceFn } from '@vueuse/core'
  import { TravelDirection, type Locale } from '@/shared/types/common.types'
  import { useWinterSport, useGoogleAnalytics, usePerformanceTiming, useEnvironment } from '@/shared/composables'
  import type { AnalyticsCategory } from '@/shared/services/GoogleAnalyticsService'
  import { formatEmiratesId } from '@/shared/utils/EmiratesId'
  import {
    COVID_ADDON_ID,
    CYBER_ADDON_ID,
    WINTER_SPORTS_ADDON_ID,
  } from '@/shared/constants/addons.constants'
  import { parseDateFromStr, toFixedNumber, formatDateDefault } from '@/shared/utils'
  import {
    OUTBOUND_MULTI_TRIP_RETURN_DAY,
    PLAN_NAMES_SCHEME_TYPE_CODE_MAP,
  } from '@/shared/constants/plans.constants'
  import { APPLE_PAY_PAYMENT_OPTIONS } from '@/shared/constants/api.constants'
  import { FlowQuoteCoverageQC } from '@/entities/Flow/components/FlowQuoteCoverageQC'
  import { FlowQuoteCoverageOoredooQC } from '@/entities/Flow/components/FlowQuoteCoverageOoredooQC'
  import { OOREDOO_SCHEME_CODE_DEV, OOREDOO_SCHEME_CODE_PROD } from '@/shared/constants/ooredoo.constants'
  import { isAppleOS } from '@/shared/utils/ApplePay'
  import { InformationCardQC } from './components/InformationCardQC'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'
  import { PreferredCoinsReward } from '@/shared/store/userStore'
  import { OoredooBanner } from './components/OoredooBanner'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import CellQC from '@qicgroupdoha/qic-connect-ds/components/CellQC'
  import { InsuredTravelers } from './components/InsuredTravelers'
  import { AddonsBlockQC } from './components/AddonsBlockQC'
  import { PaymentBlockQC } from '@qicgroupdoha/travel-devkit/components'
  import SkeletonQC from '@qicgroupdoha/qic-connect-ds/components/SkeletonQC'

  import '@qicgroupdoha/travel-devkit/style.css'

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
    withBackButton: false,
  })
  const emit = defineEmits<Emits>()

  const CoinsRewardOptions = defineAsyncComponent(() =>
    import('./components/CoinsRewardOptions').then((module) => module.CoinsRewardOptions)
  )
  const CoinsReward = defineAsyncComponent(() =>
    import('./components/CoinsRewardsQC').then((module) => module.CoinsRewardQC)
  )
  const CoinsDiscount = defineAsyncComponent(() =>
    import('./components/CoinsRewardsQC').then((module) => module.CoinsDiscountQC)
  )
  const TicketBlockQC = defineAsyncComponent(() =>
    import('@/shared/components/TicketBlockQC').then((module) => module.TicketBlockQC)
  )

  // TODO: REFACTOR IT
  interface Props {
    typeOfTravel?: TravelDirection
    withBackButton?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME, nextStep?: AvailableFlowRoutes): void

    (
      e: typeof LOAD_EVENT_NAME,
      request: () => Promise<any>,
      requestId?: string,
      withLoader?: boolean,
      withError?: boolean,
      infinityLoader?: boolean
    ): void

    (e: 'back'): void
  }

  const { isDevelopment } = useEnvironment()
  const route = useRoute()
  const { getFormData } = useFormDataWithRedirect()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const quoteStore = useQuoteStore()
  const travelersStore = useTravelersStore()
  const featuresStore = useFeaturesStore()
  const { withWinterSport } = useWinterSport()
  const { t, locale } = useI18n()
  const quote = ref<GetTravelQuoteUaeResponse | null>(null)
  const paymentOptions = ref<PaymentOption[]>([])
  const selectedPaymentOption = ref(0)
  const travelers = ref<any[]>([])
  const totalPrice = ref(0)
  const listOfDestionations = ref<Country[]>([])
  const termConditionsLink = ref('#')
  const gaService = useGoogleAnalytics()
  const { onPageReady, onPerformanceCheckEnd } = usePerformanceTiming()
  const numberAddons = ref<string[]>([])
  const isFinalPriceLoading = ref(false)
  const isPriceLoading = ref(false)

  const translationForPaymentBlock = {
    acceptTermsText2: undefined,
    naps: t('checkout.payment_block.naps_debit.title'),
    creditCard: t('checkout.payment_block.credit_card.title'),
    applePay: t('checkout.payment_block.apple_pay.title'),
    googlePay: t('checkout.payment_block.google_pay.title'),
    termsText: t('checkout.payment_block.checkbox_confirmation.text'),
    termsLink: t('checkout.payment_block.checkbox_confirmation.link'),
    paymentButton: t('qic_quote_checkout_button'),
    acceptTermsText1: t('checkout.payment_block.confirmation.text'),
    acceptTermsLink: t('checkout.payment_block.confirmation.link'),
    sslReminder: t('checkout.payment_block.security_informing.text'),
  }

  const isRamadanCampaign = inject('isRamadanCampaign') as ComputedRef<boolean>
  const alertContainerRef = ref<InstanceType<typeof AlertContainer> | null>(null)

  const isOutbound = computed(() => props.typeOfTravel === TravelDirection.OUTBOUND)
  const covidAddon = computed(
    () => selectedPlan.value?.ids.find((addon) => addon.id === COVID_ADDON_ID) ?? null
  )
  const stringQuoteNo = computed(() => (quote.value?.quoteno ? String(quote.value.quoteno) : ''))
  const withCovid = ref(userStore.withCovid)
  const winterSportExtension = computed(
    () => selectedPlan.value?.ids.find((addon) => addon.id === WINTER_SPORTS_ADDON_ID) ?? null
  )

  const withCyber = ref(userStore.withCyber)
  const cyberAddon = computed(
    () => selectedPlan.value?.ids.find((addon) => addon.id === CYBER_ADDON_ID) ?? null
  )

  const isApplePaySelected = computed(() => selectedPaymentOption.value === PAYMENT_METHOD_VALUE.AP)

  const selectedPlan = computed(() =>
    quote.value?.schemes.find((scheme) => scheme.schemecode === userStore.selectedPlan)
  )

  const OOREDOO_SCHEME_CODE = isDevelopment.value ? OOREDOO_SCHEME_CODE_DEV : OOREDOO_SCHEME_CODE_PROD
  const isOoredooPlan = computed(() => selectedPlan.value?.schemecode === OOREDOO_SCHEME_CODE)

  const canApplyCoinsDiscount = computed(
    () =>
      !!quoteStore.quoteWithCoinsDiscount &&
      !!selectedPlan.value?.coinsDiscountValue &&
      !isOoredooPlan.value
  )

  const shouldApplyCoinsDiscount = computed(
    () => canApplyCoinsDiscount.value && userStore.preferredReward === PreferredCoinsReward.SPEND
  )

  const availablePaymentMethods = computed(
    () => paymentOptions.value.map((option) => option.paymtType) ?? []
  )

  watch(canApplyCoinsDiscount, (newVal) => {
    if (newVal) {
      userStore.setPreferredReward(PreferredCoinsReward.SPEND, true)
    } else {
      userStore.setPreferredReward(PreferredCoinsReward.EARN, true)
    }
  })

  const getTravelQuoteUae = async () => {
    const destinationIds = isOutbound.value
      ? userStore.destination.map((country) => country?.id)
      : []
    if (isOutbound.value && destinationIds.length === 0) {
      return Promise.reject('Destination list is empty. Did you skip the first step?')
    }

    let type: PromoCodeTypes | undefined

    if (isOutbound.value && userStore.isAffinity && userStore.promocode) {
      type = PromoCodeTypes.AFFINITY
    }

    quote.value = await quoteStore.getTravelQuoteUae(
      userStore.promocode ?? '',
      1,
      destinationIds,
      type
    )

    quoteStore.setQuoteNo(quote.value.quoteno, true)
    quoteStore.setQuoteWithCoinsDiscount(quote.value.quoteWithCoinsDiscount, true)
    quoteStore.setCoinsDiscountPrice(quote.value.coinsDiscountPrice, true)
    if (quote.value.quoteWithCoinsDiscount) {
      userStore.setPreferredReward(PreferredCoinsReward.EARN, true)
    }
  }

  const getTravelers = async () => {
    const response = await travelersStore.getTravelers()

    travelers.value = [
      {
        srNo: 'holder',
        firstName: userStore.firstName,
        lastName: userStore.familyName,
        dateOfBirth: userStore.dateOfBirth,
        relationCode: undefined,
        relationName: undefined,
        nationalityCode: userStore.nationality?.nationalityCode,
        nationalityName: userStore.nationality?.nationalityName,
        passportNo: userStore.passportNumber,
        winSports: withWinterSport.value && isOutbound.value ? userStore.withWinterSport : false,
        emirateId: userStore.emiratesId ? formatEmiratesId(userStore.emiratesId) : null,
        civilIdExpiryDate: userStore.civilIdExpiryDate,
        email: userStore.email,
        phone: userStore.maskedPhone,
        gender: userStore.gender ?? '',
      },
      ...response.map((traveler) => ({
        ...traveler,
        dateOfBirth: parseDateFromStr(traveler.dateOfBirth),
        winSports: withWinterSport.value && isOutbound.value ? !!traveler.winSports : false,
      })),
    ]
    quoteStore.setInsuredTravelers(travelers.value.length, true)
  }

  const shouldDisplayAppleButton = (paymentType: string) =>
    APPLE_PAY_PAYMENT_OPTIONS.includes(paymentType) && isAppleOS()

  const getPaymentOptions = async () => {
    const quoteNo = stringQuoteNo.value

    if (quoteNo) {
      paymentOptions.value = await commonStore
        .getPaymentOptions(quoteNo, locale.value)
        .then((res) =>
          res.sort((item1) => {
            if (shouldDisplayAppleButton(item1.paymtType)) {
              return -1
            }

            return 0
          })
        )

      setDefaultPaymentOption(paymentOptions.value)
    }
  }

  const setDefaultPaymentOption = (options: PaymentOption[]) => {
    let resultType = ''

    if (options.length) {
      resultType = options[0].paymtType
    }

    for (const opt of options) {
      if (opt.paymtType === 'CC') {
        resultType = opt.paymtType
      }

      if (shouldDisplayAppleButton(opt.paymtType)) {
        resultType = opt.paymtType
        break
      }
    }

    selectedPaymentOption.value =
      PAYMENT_METHOD_VALUE[resultType as keyof typeof PAYMENT_METHOD_VALUE] ??
      PAYMENT_METHOD_VALUE.CC
  }

  const getTotalPrice = async (selectProduct: 0 | 1 = 0) => {
    const payload: GetTravelPricePayload = {
      quoteno: stringQuoteNo.value,
      schemecode: selectedPlan.value?.schemecode ?? '',
      productcode: selectedPlan.value?.productcode ?? '',
      commonAddons: [],
      personalAddons: [],
      selectproduct: selectProduct,
    }

    if (shouldApplyCoinsDiscount.value) {
      payload.quoteWithCoinsDiscount = quoteStore.quoteWithCoinsDiscount
        ? String(quoteStore.quoteWithCoinsDiscount)
        : ''
    }

    if (isOutbound.value) {
      if (covidAddon.value) {
        payload.commonAddons.push({
          id: COVID_ADDON_ID,
          isActive: withCovid.value,
        })
      }

      for (const traveler of travelers.value) {
        payload.personalAddons.push({
          srNo: traveler.srNo === 'holder' ? 0 : traveler.srNo,
          addons: [
            {
              id: WINTER_SPORTS_ADDON_ID,
              isActive: traveler.winSports,
            },
          ],
        })
      }
    }

    if (cyberAddon.value) {
      payload.commonAddons.push({
        id: CYBER_ADDON_ID,
        isActive: withCyber.value,
      })
    }

    const response = await quoteStore.getTravelTotalPrice(payload)
    totalPrice.value = response.totalprice
    quoteStore.setProductBonus(response.totalBonus ?? 0, true)
  }

  const getFinalQuotePrice = async (isApplePay = false) => {
    try {
      isFinalPriceLoading.value = true
      await getTotalPrice(1)
      return true
    } catch (error) {
      console.error('getFinalQuotePrice error', error)
      isFinalPriceLoading.value = false
      return false
    } finally {
      if (isApplePay) {
        isFinalPriceLoading.value = false
      }
    }
  }

  const getWordings = async () => {
    const response = await commonStore.getQatarWordings(
      7,
      props.typeOfTravel === TravelDirection.OUTBOUND
        ? WordingDocName.POLICY_WORDING
        : WordingDocName.POLICY_WORDING_INBOUND
    )
    termConditionsLink.value = response[0].Link
  }

  const getDestinations = async () => {
    const boundType = TravelDirection.OUTBOUND ? CountryBoundType.OUTBUND : CountryBoundType.INBUND
    listOfDestionations.value = await userStore.fetchCountries(boundType, locale.value)
  }

  const informationCardTitle = computed(() => {
    if (isOutbound.value) {
      if (!selectedPlan.value) {
        return ''
      }

      if (isOoredooPlan.value) {
        return t('outbound.select_plan.insurance_values.plan.ooredoo.name')
      }

      return (
        t(PLAN_NAMES_SCHEME_TYPE_CODE_MAP[selectedPlan.value.schemeTypeCode]) ??
        selectedPlan.value.name
      )
    }

    return t('travel.inbound.quote.plan.name')
  })

  const destinationText = computed(() => {
    if (isOutbound.value) {
      if (isOoredooPlan.value) {
        return t('outbound.select_plan.insurance_values.destinations_list.name')
      }

      const ids = userStore.destination.map((el) => el.id)
      return listOfDestionations.value
        .filter((el) => ids.includes(el.id))
        .map((el) => el.name)
        .join(', ')
    }

    const id = userStore.residenceCountry?.id
    return listOfDestionations.value.find((el) => el.id === id)?.name || ''
  })

  const informationCardOptions = computed(() => {
    let returnDate = userStore.returnDate ? new Date(userStore.returnDate) : null
    const departureDate = userStore.departureDate ? new Date(userStore.departureDate) : null
    if (userStore.isOneYearPolicySelected && departureDate) {
      returnDate = new Date(
        new Date(departureDate).setDate(departureDate.getDate() + OUTBOUND_MULTI_TRIP_RETURN_DAY)
      )
    }

    const res = [
      {
        title: t('travel.outbound.quote.insurance_period.name'),
        content:
          userStore.departureDate && returnDate
            ? `${formatDateDefault(userStore.departureDate as Date, locale.value as Locale)} - ${formatDateDefault(
                returnDate,
                locale.value as Locale
              )}`
            : '',
      },
      {
        title: isOutbound.value
          ? t('travel.outbound.quote.destination.name')
          : t('travel.inbound.quote.country.name'),
        content: destinationText.value,
      },
    ]

    return res
  })

  const winterSportExtensionPrice = computed(() =>
    winterSportExtension.value?.value
      ? winterSportExtension.value.value / travelers.value.length
      : 0
  )

  const onEditTravelers = () => {
    const ids = userStore.destination.map((el) => el.id)

    gaService.sendEditTravellersQuote({
      category: props.typeOfTravel as AnalyticsCategory,
      numOfTravelers: travelersCount,
      quoteId: quote.value?.quoteno || 0,
      totalPrice: totalPrice.value,
      countries: listOfDestionations.value.filter((el) => ids.includes(el.id)).map((el) => el.name),
    })

    emit('back')
  }

  const addonsPrice = computed(() => {
    let price = 0
    if (withCovid.value && covidAddon.value) price += covidAddon.value.value

    price +=
      Object.values(travelers.value).filter(
        (traveler) => traveler.winSports && withWinterSport.value
      ).length * winterSportExtensionPrice.value
    return Math.round(price)
  })

  const travelersWithWinterSport = computed(() =>
    Object.values(travelers.value).filter((traveler) => traveler.winSports && withWinterSport.value)
  )

  const winterSportPrice = computed(() => {
    const price = travelersWithWinterSport.value.length
      ? travelersWithWinterSport.value.length * winterSportExtensionPrice.value
      : winterSportExtensionPrice.value

    return Math.round(price)
  })

  const activeAddonsCount = computed(() => {
    const covidCount = withCovid.value && covidAddon.value ? 1 : 0
    const travelersWinterSportCount = travelers.value.filter(
      (traveler) => traveler.winSports && withWinterSport.value
    ).length

    return travelersWinterSportCount + covidCount
  })

  const setAddonsPrice = () => {
    quoteStore.setAddonsPrice(addonsPrice.value, true)
  }

  const setActiveAddons = () => {
    quoteStore.setActiveAddons(activeAddonsCount.value, true)
  }

  const onCovidChange = (value: boolean) => {
    withCovid.value = value
    userStore.setwithCovid(withCovid.value, true)
    if (covidAddon.value) {
      onToggleAddon(covidAddon.value.id, withCovid.value, 'holder')
    }
  }

  const onCyberChange = (value: boolean) => {
    withCyber.value = value
    userStore.setWithCyber(withCyber.value, true)
    if (cyberAddon.value) {
      onToggleAddon(cyberAddon.value.id, withCyber.value, 'holder')
    }
  }

  const onToggleAddon = useDebounceFn(async (addonId: string, isChecked: boolean, srNo: string) => {
    try {
      isPriceLoading.value = true

      if (addonId === WINTER_SPORTS_ADDON_ID) {
        travelers.value = travelers.value.map((traveler) => {
          if (traveler.srNo === srNo) {
            traveler.winSports = isChecked
          }

          return traveler
        })

        if (srNo === 'holder') {
          userStore.setWithWinterSport(isChecked, true)
          gaService.sendSwitchAddon({
            addon_name: 'winter sport',
            quote_id: stringQuoteNo.value,
            addon_price: toFixedNumber(winterSportPrice.value),
            state: isChecked,
            person_name: `${userStore.firstName} ${userStore.familyName}`,
            index: '0',
            num_of_travelers: String(travelers.value.length),
          })
        } else {
          const traveler = travelers.value.find((traveler) => traveler.srNo === srNo)
          if (!traveler) return

          gaService.sendSwitchAddon({
            addon_name: 'winter sport',
            quote_id: stringQuoteNo.value,
            addon_price: toFixedNumber(winterSportPrice.value),
            state: isChecked,
            person_name: `${traveler.firstName} ${traveler.lastName}`,
            index: srNo,
            num_of_travelers: String(travelers.value.length),
          })
        }
      } else if (addonId === COVID_ADDON_ID) {
        gaService.sendSwitchAddon({
          addon_name: 'COVID-19',
          quote_id: stringQuoteNo.value,
          addon_price: toFixedNumber(isChecked ? (covidAddon.value?.value ?? 0) : 0),
          state: isChecked,
        })
      } else {
        gaService.sendSwitchAddon({
          addon_name: 'cyber_security',
          quote_id: stringQuoteNo.value,
          addon_price: toFixedNumber(
            isChecked && cyberAddon.value ? (cyberAddon.value.value ?? 0) : 0
          ),
          state: isChecked,
          category: props.typeOfTravel,
        })
      }

      setAddonsPrice()
      setActiveAddons()
      setAddonsList()

      await getTotalPrice()
    } catch {
      onToggleAddonError(addonId, isChecked, srNo)
    } finally {
      isPriceLoading.value = false
    }
  }, 100)

  const onToggleAddonError = (addonId: string, isChecked: boolean, srNo: string) => {
    const prevValue = !isChecked

    alertContainerRef.value?.add(t('outbound.addon.error'), 5000)

    if (addonId === WINTER_SPORTS_ADDON_ID) {
      if (srNo === 'holder') {
        userStore.setWithWinterSport(prevValue, true)
      }

      travelers.value = travelers.value.map((traveler) => {
        if (traveler.srNo === srNo) {
          traveler.winSports = prevValue
        }

        return traveler
      })
    }

    if (addonId === COVID_ADDON_ID) {
      withCovid.value = prevValue
    }

    if (addonId === CYBER_ADDON_ID) {
      withCyber.value = prevValue
    }
  }

  const onPaymentError = (error: string) => {
    isFinalPriceLoading.value = false
    console.log(error ?? 'The selected payment option is not valid!')
  }

  const setPaymentMethod = () => {
    let paymentMethod = ''
    if (selectedPaymentOption.value === PAYMENT_METHOD_VALUE.DC) {
      paymentMethod = 'naps'
    } else if (isApplePaySelected.value) {
      paymentMethod = 'apple pay'
    } else {
      paymentMethod = 'credit-card'
    }

    userStore.setPaymentMethod(paymentMethod, true)
  }

  const onBuy = async () => {
    await emit(
      LOAD_EVENT_NAME,
      async () => {
        const selectedType = PAYMENT_VALUE_TO_TYPE[selectedPaymentOption.value]
        const redirectUrl = PAYMENT_REDIRECT_HASH[selectedType]
        if (!redirectUrl && !isApplePaySelected.value) {
          throw new Error('No payment selected!')
        }

        userStore.setLastFlowDirection(props.typeOfTravel, true)
        setPaymentMethod()
        quoteStore.setIsSentPurchaseAnalyticsEvent(false, true)

        if (props.typeOfTravel === TravelDirection.INBOUND) {
          gaService.sendQuoteBuy(
            userStore.promocode ?? '',
            quote.value?.quoteno ?? 0,
            travelers.value.length,
            totalPrice.value,
            [userStore.residenceCountry?.name ?? ''],
            userStore.paymentMethod ?? ''
          )
        } else if (props.typeOfTravel === TravelDirection.OUTBOUND) {
          const coinsDiscountParams = canApplyCoinsDiscount.value
            ? {
                tabTitle: userStore.preferredReward,
                discountPrice:
                  userStore.preferredReward === PreferredCoinsReward.SPEND
                    ? toFixedNumber(selectedPlan.value?.coinsDiscountValue || 0)
                    : undefined,
              }
            : {}

          gaService.sendOutboundQuoteBuy(
            userStore.promocode ?? '',
            quote.value?.quoteno ?? 0,
            travelers.value.length,
            addonsPrice.value,
            totalPrice.value,
            quoteStore.addonsList,
            userStore.destination.map((country) => country.id),
            userStore.paymentMethod ?? '',
            quoteStore.activeAddons,
            coinsDiscountParams.tabTitle,
            coinsDiscountParams.discountPrice
          )
        }

        if (isApplePaySelected.value) {
          isFinalPriceLoading.value = true
          return emit(
            PROCEED_EVENT_NAME,
            isOutbound.value
              ? OutboundFlowRoutes.INSURANCE_RESULT
              : InboundFlowRoutes.INSURANCE_RESULT
          )
        } else {
          await getFinalQuotePrice()
        }
        window.location.href = redirectUrl
      },
      'payment',
      false,
      false,
      true
    )
  }

  const addonsList = ref<string[]>([])
  const setAddonsList = () => {
    if (covidAddon.value) {
      if (withCovid.value && !addonsList.value.includes(covidAddon.value?.name)) {
        addonsList.value.push(covidAddon.value.name)
      } else if (!withCovid.value) {
        addonsList.value = addonsList.value.filter((item) => item !== covidAddon.value?.name)
      }
    }
    const winterSportCount = travelers.value.filter(
      (traveler) => traveler.winSports && withWinterSport.value
    ).length

    if (!winterSportCount) {
      addonsList.value = addonsList.value.filter(
        (item) => item !== winterSportExtension.value?.name
      )
    } else {
      if (
        winterSportExtension.value?.name &&
        !addonsList.value.includes(winterSportExtension.value.name)
      ) {
        addonsList.value.push(winterSportExtension.value?.name)
      }
    }

    quoteStore.setAddonsList(addonsList.value, true)
  }

  watch(
    () => totalPrice.value,
    (newValue) => {
      quoteStore.setTotalPrice(newValue, true)
    }
  )

  const onPaymentTermsClick = () => {
    gaService.sendPaymentTermsConditions(route.name as AvailableFlowRoutes, stringQuoteNo.value)
  }

  const paymentDescriptionForPopup = () =>
    paymentOptions.value.find(
      (o) => o.paymtType === PAYMENT_VALUE_TO_TYPE[selectedPaymentOption.value]
    )?.paymCond

  const travelersCount = userStore.adultsCount + userStore.childrenCount

  const onTravellersExpand = () => {
    gaService.sendTravellersToggleQuote({
      category: props.typeOfTravel as AnalyticsCategory,
      numOfTravelers: travelersCount,
      quoteId: quote.value?.quoteno || 0,
      totalPrice: totalPrice.value,
    })
  }

  const onChooseCoinsReward = useDebounceFn(async (option: PreferredCoinsReward) => {
    userStore.setPreferredReward(option, true)
    isPriceLoading.value = true

    try {
      await getTotalPrice()
      gaService.sendCoinsRewardTabClick(
        option,
        props.typeOfTravel,
        travelers.value.length,
        quote.value?.quoteno || 0,
        totalPrice.value,
        option === PreferredCoinsReward.SPEND
          ? toFixedNumber(selectedPlan.value?.coinsDiscountValue ?? 0)
          : undefined
      )
    } finally {
      isPriceLoading.value = false
    }
  }, 100)

  onMounted(() => {
    emit(LOAD_EVENT_NAME, async () => {
      onPageReady()

      if (!(await getFormData(props.typeOfTravel, true))) return
      await Promise.all([getDestinations(), getWordings(), getTravelers(), getTravelQuoteUae()])
      await getTotalPrice()
      await getPaymentOptions()
      setAddonsPrice()
      setActiveAddons()
      setAddonsList()
      ;(withCovid.value && covidAddon.value ? numberAddons.value.push(covidAddon.value?.id) : null,
        travelers.value.some((traveler) => traveler.winSports)
          ? numberAddons.value.push(winterSportExtension.value?.id ?? '')
          : null)

      gaService.sendViewQuote(
        route.name as AvailableFlowRoutes,
        isOutbound.value
          ? userStore.destination.map((country) => country.id)
          : [userStore.residenceCountry?.name ?? ''],
        quoteStore.addonsList,
        quoteStore.activeAddons,
        String(travelers.value.length),
        addonsPrice.value,
        stringQuoteNo.value,
        totalPrice.value
      )

      onPerformanceCheckEnd()
    })
  })
</script>

<template>
  <WrapperQC class="quote-qc" direction="column" gap="var(--spacing-7)">
    <FlowCardQC
      :title="t('check_quote.header')"
      :with-back-button="withBackButton"
      :disabled-back-button="isFinalPriceLoading"
      @back="emit('back')"
    >
      <InformationCardQC :title="informationCardTitle" :options="informationCardOptions" />
    </FlowCardQC>

    <InsuredTravelers
      v-if="travelers.length"
      :travelers="travelers"
      :type-of-travel="props.typeOfTravel"
      :is-loading="isFinalPriceLoading"
      @toggle="onTravellersExpand"
      @edit="onEditTravelers"
    />

    <FlowQuoteCoverageQC
      v-if="selectedPlan && !isOoredooPlan"
      :plan="selectedPlan"
      :travelers-count="travelers?.length ?? 0"
    />
    <FlowQuoteCoverageOoredooQC v-else-if="selectedPlan && isOoredooPlan" />

    <AddonsBlockQC
      v-if="covidAddon?.value && !isOoredooPlan"
      :is-active="withCovid"
      data-test="covid-cover-qc"
      variant="covid"
      :title="$t('outbound_covid_cover_name')"
      :description="$t('outbound_covid_cover_description')"
      :is-price-loading="isPriceLoading"
      :disabled="isFinalPriceLoading"
      :price="covidAddon?.value"
      :travelers="travelers"
      @update:is-active="onCovidChange"
    />

    <AddonsBlockQC
      v-if="winterSportExtensionPrice && !isOoredooPlan"
      variant="winter-sports"
      :title="$t('outbound_sport_cover_winter_name')"
      :description="$t('outbound_sport_cover_winter_description')"
      :is-price-loading="isPriceLoading"
      :disabled="isFinalPriceLoading"
      :price="winterSportExtensionPrice"
      :travelers="travelers"
      @toggle-winter-sports="
        (isChecked, srNo) => onToggleAddon(winterSportExtension?.id ?? '', isChecked, srNo)
      "
    />

    <AddonsBlockQC
      v-if="cyberAddon?.value && !isOoredooPlan"
      :is-active="withCyber"
      data-test="cyber-security-cover-qc"
      variant="cyber"
      :title="$t('outbound.cyber_security_addon.name')"
      :description="$t('outbound.cyber_security_addon.desc')"
      :is-price-loading="isPriceLoading"
      :disabled="isFinalPriceLoading"
      :price="cyberAddon?.value"
      :quote-id="quoteStore.quoteNo"
      :travelers="travelers"
      @update:is-active="onCyberChange"
      @add-cyber-cover="onCyberChange(true)"
    />

    <OoredooBanner v-if="featuresStore.isOoredooEnabled && !isOoredooPlan" />

    <FlowCardQC class="quote-payment-qc">
      <WrapperQC direction="column" gap="var(--spacing-7)" class="quote-payment-qc__wrapper">
        <CoinsRewardOptions
          v-if="canApplyCoinsDiscount"
          :active-option="userStore.preferredReward"
          :coins-reward="quoteStore.productBonus"
          :coins-price="quoteStore.coinsDiscountPrice ?? 0"
          :loading="isPriceLoading"
          @choose-reward="onChooseCoinsReward"
        />

        <WrapperQC direction="column" gap="var(--spacing-4)" class="quote-payment-qc__inner">
          <WrapperQC v-if="!isOoredooPlan" direction="column" class="quote-payment-qc__inner-item">
            <CellQC
              v-if="userStore.promocode"
              padding-block="spacing-2"
              class="quote-payment-qc__row"
              :title="$t('checkout.payment_block.promo_code.title')"
              :value="userStore.promocode"
            />

            <CellQC
              padding-block="spacing-2"
              class="quote-payment-qc__row"
              :title="$t('travel.common.quote.inclusive_cover.name')"
            >
              <template #value>
                <SkeletonQC v-if="isPriceLoading" width="109px" height="24px" />
                <div v-else class="ltr">
                  {{ $t('qar') }}
                  {{ toFixedNumber((selectedPlan?.price ?? 0) + (selectedPlan?.discount ?? 0)) }}
                </div>
              </template>
            </CellQC>

            <CellQC
              v-if="isOutbound && addonsPrice"
              class="quote-payment-qc__row"
              padding-block="spacing-2"
              :title="$t('checkout.payment_block.addons.title_new')"
            >
              <template #value>
                <SkeletonQC v-if="isPriceLoading" width="109px" height="24px" />
                <div v-else class="ltr">{{ $t('qar') }} {{ addonsPrice }}</div>
              </template>
            </CellQC>

            <CellQC
              v-if="withCyber && cyberAddon"
              class="quote-payment-qc__row"
              padding-block="spacing-2"
              :title="$t('outbound.cyber_security_addon.name')"
            >
              <template #value>
                <SkeletonQC v-if="isPriceLoading" width="109px" height="24px" />
                <div v-else class="ltr">{{ $t('qar') }} {{ toFixedNumber(cyberAddon.value) }}</div>
              </template>
            </CellQC>

            <CoinsDiscount
              v-if="shouldApplyCoinsDiscount"
              :is-price-loading="isPriceLoading"
              :amount="toFixedNumber(selectedPlan?.coinsDiscountValue ?? 0)"
            />

            <template v-for="discount in selectedPlan?.discounts ?? []" :key="discount.name">
              <CellQC
                v-if="discount.premium"
                padding-block="spacing-2"
                class="quote-payment-qc__row"
                :title="discount.name"
              >
                <template #value>
                  <SkeletonQC v-if="isPriceLoading" width="109px" height="24px" />
                  <div v-else class="quote-payment-qc__discount-value">
                    &#x200E;−
                    {{ $t('qar_price', { price: toFixedNumber(Number(discount.premium)) ?? 0 }) }}
                  </div>
                </template>
              </CellQC>
            </template>
          </WrapperQC>

          <WrapperQC v-if="totalPrice" direction="column" class="quote-payment-qc__inner-item">
            <CellQC
              padding-block="spacing-2"
              class="quote-payment-qc__row quote-payment-qc__total"
              title-color="primary"
            >
              <template #title>
                <p class="heading-5">
                  {{ $t('checkout.payment_block.total.title') }}
                </p>
              </template>
              <template #value>
                <SkeletonQC v-if="isPriceLoading" width="109px" height="24px" />
                <div v-else class="ltr heading-5">
                  {{ $t('qar') }} {{ toFixedNumber(totalPrice) }}
                </div>
              </template>
            </CellQC>
          </WrapperQC>

          <WrapperQC
            v-if="quoteStore.productBonus > 0 && !shouldApplyCoinsDiscount && !isOoredooPlan"
            direction="column"
            class="quote-payment-qc__inner-item"
          >
            <CoinsReward :is-price-loading="isPriceLoading" :amount="quoteStore.productBonus" />
          </WrapperQC>

          <WrapperQC
            v-if="isRamadanCampaign"
            direction="column"
            class="quote-payment-qc__inner-item"
          >
            <TicketBlockQC />
          </WrapperQC>
        </WrapperQC>
      </WrapperQC>

      <WrapperQC
        direction="column"
        gap="var(--spacing-5)"
        :padding="{
          blockStart: 'var(--spacing-5)',
        }"
      >
        <CellQC title-color="primary" padding-block="spacing-2">
          <template #title>
            <p class="heading-6">
              {{ $t('checkout.payment_block.select_method.title') }}
            </p>
          </template>
        </CellQC>

        <!-- @vue-ignore -->
        <PaymentBlockQC
          v-if="paymentOptions.length"
          v-model:payment-method="selectedPaymentOption"
          class="payment-block-qc"
          data-test="payment-block-qc"
          :payment-options="paymentOptions"
          :total-price="totalPrice"
          :is-loading="isPriceLoading || isFinalPriceLoading"
          :quote-number="stringQuoteNo"
          :terms-href="termConditionsLink"
          :available-payment-methods="availablePaymentMethods"
          :on-before-pay-apple-pay="() => getFinalQuotePrice(true)"
          :locale="locale"
          :i18n="translationForPaymentBlock"
          @click-terms="gaService.sendTermsConditions(route.name, quote?.quoteno)"
          @click-checkout="onBuy"
          @open-terms-popup="onPaymentTermsClick"
          @error="onPaymentError"
        >
          <template #modal-title>{{ t('payment_terms_popup.heading') }}</template>

          <template #modal-content>
            {{ paymentDescriptionForPopup() }}
          </template>
        </PaymentBlockQC>
      </WrapperQC>
    </FlowCardQC>

    <AlertContainer ref="alertContainerRef" />
  </WrapperQC>
</template>

<style scoped lang="scss">
  @use './FlowQuoteWidgetQC';
</style>
